import React, { useState, useEffect, useMemo } from "react";
import Select from "react-select";

export const STATUS_OPTIONS = [
  { value: "saved",   label: "Saved" },
  { value: "paid",    label: "Paid" },
  { value: "overdue", label: "Overdue" },
];

export const DATE_PRESETS = [
  { value: "last30",      label: "Last 30 Days" },
  { value: "thisQuarter", label: "This Quarter" },
  { value: "thisYear",    label: "This Year" },
  { value: "custom",      label: "Custom Range" },
];

export function computeDateRange(preset) {
  const today = new Date();
  const fmt = (d) => d.toISOString().split("T")[0];
  if (preset === "last30") {
    const from = new Date(today); from.setDate(from.getDate() - 30);
    return { dateFrom: fmt(from), dateTo: fmt(today) };
  }
  if (preset === "thisQuarter") {
    const qStart = new Date(today.getFullYear(), Math.floor(today.getMonth() / 3) * 3, 1);
    return { dateFrom: fmt(qStart), dateTo: fmt(today) };
  }
  if (preset === "thisYear") {
    return { dateFrom: `${today.getFullYear()}-01-01`, dateTo: fmt(today) };
  }
  return { dateFrom: "", dateTo: "" };
}

export const selectStyles = {
  control: (base, state) => ({
    ...base, fontSize: "0.83rem", minHeight: 36,
    borderColor: state.isFocused ? "#002045" : "#e5e7eb",
    boxShadow: state.isFocused ? "0 0 0 1px #002045" : "none",
    "&:hover": { borderColor: "#002045" },
  }),
  multiValue:       (base) => ({ ...base, background: "#dce8ff", borderRadius: 4 }),
  multiValueLabel:  (base) => ({ ...base, color: "#002045", fontSize: "0.75rem", fontWeight: 600 }),
  multiValueRemove: (base) => ({ ...base, color: "#002045", "&:hover": { background: "#002045", color: "white" } }),
  option: (base, state) => ({
    ...base, fontSize: "0.83rem",
    background: state.isSelected ? "#002045" : state.isFocused ? "#f0f4ff" : "white",
    color: state.isSelected ? "white" : "#374151", cursor: "pointer",
  }),
  placeholder: (base) => ({ ...base, fontSize: "0.83rem", color: "#9ca3af" }),
  menu:        (base) => ({ ...base, zIndex: 1070 }),
};

export const PhaseSelect = ({ phases, value, onChange }) => {
  const options = useMemo(() => (phases || []).map((p) => ({ value: String(p.id), label: p.name })), [phases]);
  const selected = options.find((o) => o.value === String(value)) || null;
  return (
    <Select options={options} value={selected} onChange={(opt) => onChange(opt ? opt.value : "")}
      placeholder="All phases..." isClearable styles={selectStyles} classNamePrefix="rs" />
  );
};

const DeepFilterPanel = ({ filters, onChange }) => {
  const [localMin, setLocalMin] = useState(filters.amountMin || "");
  const [localMax, setLocalMax] = useState(filters.amountMax || "");

  useEffect(() => { setLocalMin(filters.amountMin || ""); }, [filters.amountMin]);
  useEffect(() => { setLocalMax(filters.amountMax || ""); }, [filters.amountMax]);

  const selectedStatuses = STATUS_OPTIONS.filter((o) => (filters.statuses || []).includes(o.value));

  const handleDatePreset = (preset) => {
    if (preset === filters.datePreset) { onChange({ ...filters, datePreset: "", dateFrom: "", dateTo: "" }); return; }
    if (preset === "custom") { onChange({ ...filters, datePreset: "custom" }); return; }
    const { dateFrom, dateTo } = computeDateRange(preset);
    onChange({ ...filters, datePreset: preset, dateFrom, dateTo });
  };

  const commitAmount = () => onChange({ ...filters, amountMin: localMin, amountMax: localMax });

  const sectionTitle = { fontSize: "0.62rem", fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.08em", color: "#8a92a0", marginBottom: 10 };
  const presetBtn = (preset) => ({
    display: "block", width: "100%", textAlign: "left",
    background: filters.datePreset === preset ? "#002045" : "white",
    color: filters.datePreset === preset ? "white" : "#374151",
    border: `1px solid ${filters.datePreset === preset ? "#002045" : "#e5e7eb"}`,
    borderRadius: 7, padding: "6px 12px", fontSize: "0.78rem", fontWeight: 500, cursor: "pointer", marginBottom: 6,
  });

  return (
    <div style={{ background: "#f8f9fb", borderBottom: "1px solid #e9ecef", padding: "20px 24px" }}>
      <div className="row g-4">
        <div className="col-md-4">
          <div style={sectionTitle}>Date Range</div>
          {DATE_PRESETS.map(({ value, label }) => (
            <button key={value} type="button" style={presetBtn(value)} onClick={() => handleDatePreset(value)}>{label}</button>
          ))}
          {filters.datePreset === "custom" && (
            <div className="d-flex gap-2 mt-2">
              <input type="date" className="form-control form-control-sm" value={filters.dateFrom}
                onChange={(e) => onChange({ ...filters, dateFrom: e.target.value })} style={{ fontSize: "0.78rem" }} />
              <input type="date" className="form-control form-control-sm" value={filters.dateTo}
                onChange={(e) => onChange({ ...filters, dateTo: e.target.value })} style={{ fontSize: "0.78rem" }} />
            </div>
          )}
        </div>
        <div className="col-md-4">
          <div style={sectionTitle}>Invoice Status</div>
          <Select isMulti options={STATUS_OPTIONS} value={selectedStatuses}
            onChange={(sel) => onChange({ ...filters, statuses: (sel || []).map((o) => o.value) })}
            placeholder="All statuses..." styles={selectStyles} classNamePrefix="rs" />
        </div>
        <div className="col-md-4">
          <div style={sectionTitle}>Amount Range</div>
          <div className="d-flex align-items-center gap-2">
            <input type="number" className="form-control form-control-sm" placeholder="Min"
              value={localMin} onChange={(e) => setLocalMin(e.target.value)}
              onBlur={commitAmount} onKeyDown={(e) => e.key === "Enter" && commitAmount()} style={{ fontSize: "0.83rem" }} />
            <span style={{ color: "#9ca3af", flexShrink: 0 }}>—</span>
            <input type="number" className="form-control form-control-sm" placeholder="Max"
              value={localMax} onChange={(e) => setLocalMax(e.target.value)}
              onBlur={commitAmount} onKeyDown={(e) => e.key === "Enter" && commitAmount()} style={{ fontSize: "0.83rem" }} />
          </div>
          {(localMin || localMax) && (
            <button type="button" onClick={() => { setLocalMin(""); setLocalMax(""); onChange({ ...filters, amountMin: "", amountMax: "" }); }}
              style={{ background: "none", border: "none", padding: 0, marginTop: 8, fontSize: "0.72rem", fontWeight: 600, color: "#6b7280", cursor: "pointer", textTransform: "uppercase", letterSpacing: "0.05em" }}>
              Clear Amount
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

export default DeepFilterPanel;
